import { StepMap } from "prosemirror-transform";
import { customKeymap } from "../keymaps/custom-keymap";
import { undo, redo } from "prosemirror-history";
import { EditorState, Selection, TextSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { menuPluginKey } from "../plugins/menu/menu-plugin";
import { keymap } from "prosemirror-keymap";

export default class GhostNodeView {
  constructor(node, view, getPos) {
    this.outerView = view;
    this.node = node;
    this.getPos = getPos;

    this.dom = document.createElement("span");
    this.dom.classList = "fantasma";
    //this.dom.setAttribute("aria-hidden", "true");
    this.dom.contentEditable = false;
    this.dom.tabIndex = 0;

    this.dispatchInnerTransaction = this.dispatchTransaction();
    this.innerView = new EditorView(this.dom, {
      state: EditorState.create({
        doc: this.node,
        plugins: [
          customKeymap,
          keymap({
            "ArrowUp": () => this.arrowHandle("line", -1),
            "ArrowDown": () => this.arrowHandle("line", 1),
            "ArrowLeft": () => this.arrowHandle("char", -1),
            "ArrowRight": () => this.arrowHandle("char", 1),
            "Ctrl-z": () => undo(this.outerView.state, this.outerView.dispatch),
            "Ctrl-y": () => redo(this.outerView.state, this.outerView.dispatch)
          })
        ]
      }),
      dispatchTransaction: this.dispatchInnerTransaction,
      handleDOMEvents: {
        mousedown: () => {
          if(!this.innerView.hasFocus()) {
            this.innerView.focus();
            this.updateCurrentView(this.innerView);
          }

          return true;
        },
      }
    });
  }

  arrowHandle(unit, direction) {
    const { state } = this.innerView,
      { empty, from, to } = state.selection;

    if(!empty) {
      return false;
    }

    if(direction < 0 ? from > 0 : to < this.node.nodeSize - 2) {
      return false;
    }

    let targetPos = this.getPos() + (direction < 0 ? 0 : this.node.nodeSize),
      selection = Selection.near(this.outerView.state.doc.resolve(targetPos), direction),
      tr = this.outerView.state.tr.setSelection(selection).scrollIntoView();
    this.outerView.dispatch(tr.setMeta("fromInside", true));
    this.outerView.focus();

    return true;
  }

  dispatchTransaction() {
    return (tr) => {
      const { state, transactions } = this.innerView.state.applyTransaction(tr);
      this.innerView.updateState(state);

      if(!tr.getMeta("fromOutside")) {
        const { tr: outerTr } = this.outerView.state,
          offsetMap = StepMap.offset(this.getPos() + 1);

        for(let i = 0; i < transactions.length; i++) {
          let { steps } = transactions[i];
          for(let j = 0; j < steps.length; j++) {
            outerTr.step(steps[j].map(offsetMap));
          }
        }

        //if(outerTr.docChanged) {
          this.outerView.dispatch(outerTr);
        //}
      }
    }
  }

  update(node) {
    if(!node.sameMarkup(this.node)) {
      return false;
    }

    this.node = node;
    const { state } = this.innerView,
      start = node.content.findDiffStart(state.doc.content);

    if(start !== null) {
      let { a: endA, b: endB } = node.content.findDiffEnd(state.doc.content),
        overlap = start - Math.min(endA, endB);

      if(overlap > 0) {
        endA += overlap;
        endB += overlap;
      }

      this.innerView.dispatch(
        state.tr.replace(start, endB, node.slice(start, endA))
        .setMeta("fromOutside", true)
      );
    }

    return true;
  }

  destroy() {
    this.dom.remove();
    this.dom = null;
    this.outerView = null;
    this.node = null;
    this.getPos = null;
    this.innerView = null;
  }

  setSelection(anchor, head, root) {
    this.innerView.focus();
    this.innerView.dispatch(
      this.innerView.state.tr
        .setSelection(TextSelection.create(this.innerView.state.doc, anchor, head))
        .setMeta("fromInside", true)
    );
  }

  stopEvent(event) {
    return this.dom.contains(event.target);
  }

  ignoreMutation() {
    return true;
  }

  selectNode() {
    this.dom.classList.add("ProseMirror-selectednode");
    this.updateCurrentView(this.innerView);
    this.innerView.focus();
  }

  deselectNode() {
    this.dom.classList.remove("ProseMirror-selectednode");
  }

  updateCurrentView(view) {
    const { tr: outerTr } = this.outerView.state;
    outerTr.setMeta(menuPluginKey, {
      action: "viewChange",
      view,
      meta: "from Details"
    });
    this.outerView.dispatch(outerTr);
  }
}
